/**
 *  [2022] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */


/**
 * :ZNOTE:
 * tag: cacheManage
 * note:
 *  - cacheManageのlatestタイムスタンプを管理する
 * :ZNOTE:
 */

import {Dexie} from 'dexie';

class CacheManageTimestamp {
  private readonly db: Dexie.Table;
  private readonly primaryKeyName: string;
  private latest: {[key:string]: number} = {};  // init()で初期化するためにpublicにしている
  private needUpdate = false;

  /**
   *
   * @param db {Dexie.Table}
   * @param primaryKeyName {string}
   */
  constructor(db: Dexie.Table, primaryKeyName: string) {
    this.db = db;
    this.primaryKeyName = primaryKeyName;

    if ((this.db as any).setClearCallback) {
      // localStorageを用いる場合、容量オーバーが比較的頻繁に発生する可能性があるので、clear時にlatestもクリアする
      (this.db as any).setClearCallback(() => this.latest = {});
    }
  }

  /**
   * 与えられたデータで初期化する
   * @param allInfo
   */
  public setLatest = (allInfo: {[key:string]: number}) => this.latest = JSON.parse(JSON.stringify(allInfo));

  /**
   * 最新のタイムスタンプを返す
   * @param key {string}
   * @returns {number}
   */
  public getLatest = (key: string): number => this.latest[key] ? this.latest[key] : 0;

  /**
   * IndexedDBにlatestの情報を書き込む
   */
  public registerLatest = async () => {
    if (this.needUpdate) {
      this.db.put({name: 'latest', value: this.latest});
      this.needUpdate = false;
    }
  };

  /**
   * updatedAtが最新の値だった場合は更新する
   * @param primaryKeyValue {string}
   * @param updatedAt {number}
   */
  public update = (primaryKeyValue: string, updatedAt: number) => {
    if (!this.latest[primaryKeyValue] || this.latest[primaryKeyValue] < updatedAt) {
      this.latest[primaryKeyValue] = updatedAt;
      this.needUpdate = true;
    }
  };

  /**
   * エントリを削除する
   * @param primaryKeyValue {string}
   */
  public remove = async (primaryKeyValue: string) => {
    if (this.latest[primaryKeyValue]) {
      delete this.latest[primaryKeyValue];
      await this.registerLatest();
    }
  };

  /**
   * 最新時刻を巻き戻す（不具合が起こったときのため）
   * @param primaryKeyValue {string}
   * @param timeToRewind {number}
   */
  public async rewindTimestamp(primaryKeyValue: string, timeToRewind: number) {
    if (!this.latest[primaryKeyValue]) return;
    this.latest[primaryKeyValue] = this.latest[primaryKeyValue] - timeToRewind > 0 ? this.latest[primaryKeyValue] - timeToRewind : 0;
    await this.db.put({name: 'latest', value: this.latest});
  }

  /**
   * 最新時刻を強制設定する
   * @param primaryKeyValue {string}
   * @param timestampMilliSec {number}
   */
  public async setLatestTimestamp(primaryKeyValue: string, timestampMilliSec: number) {
    if (!this.latest[primaryKeyValue]) return;
    this.latest[primaryKeyValue] = timestampMilliSec;
    await this.db.put({name: 'latest', value: this.latest});
  }

  /**
   * IndexedDBをすべてスキャンして、this.latestを再構築する
   *  (テスト用にpublicにしているが、本来はprivateでいい)
   * @private
   */
  public async checkAllLatestTimestamps(allEntries: any) {
    const latest: any = {};
    allEntries.forEach((ent) => {
      if (!latest[ent[this.primaryKeyName]] || latest[ent[this.primaryKeyName]] < ent.updatedAt) {
        latest[ent[this.primaryKeyName]] = ent.updatedAt;
      }
    });
    this.latest = latest;
    await this.db.put({name: 'latest', value: this.latest});
  }
}

export {CacheManageTimestamp};
